// A guided song at the player's own pace. Each step is either a note to land
// on (a target ring on the note ladder) or a basic move (rest, swell, loop).
// Pure state so it is testable without a DOM.

export type LessonStepKind = 'intro' | 'note' | 'rest' | 'swell' | 'loop' | 'done'

export interface LessonStep {
  kind: LessonStepKind
  noteIndex?: number
  lyric: string
  hint: string
}

export interface LessonObservation {
  handVisible: boolean
  playing: boolean
  noteIndex: number | null
  level: number
  loopTap: boolean
}

export interface LessonView {
  title: string
  step: LessonStep
  index: number
  total: number
  noteNumber: number | null
  noteTotal: number
  complete: boolean
}

// Ladder indices in C minor pentatonic: 1 = E♭, 2 = F, 3 = G, 4 = B♭.
const MARY_NOTES: [number, string][] = [
  [3, 'Ma'], [2, 'ry'], [1, 'had'], [2, 'a'], [3, 'lit'], [3, 'tle'], [3, 'lamb'],
  [2, 'lit'], [2, 'tle'], [2, 'lamb'],
  [3, 'lit'], [4, 'tle'], [4, 'lamb'],
  [3, 'Ma'], [2, 'ry'], [1, 'had'], [2, 'a'], [3, 'lit'], [3, 'tle'], [3, 'lamb'], [3, 'its'],
  [2, 'fleece'], [2, 'was'], [3, 'white'], [2, 'as'], [1, 'snow']
]

const note = ([noteIndex, lyric]: [number, string]): LessonStep => ({ kind: 'note', noteIndex, lyric, hint: 'Point your index finger at the glowing line. Open hand plays.' })

export const MARY_LESSON: LessonStep[] = [
  { kind: 'intro', lyric: 'Mary Had a Little Lamb', hint: 'Raise one hand, open it, and point your index finger at the glowing note line. Take your time — the song waits for you.' },
  ...MARY_NOTES.slice(0, 7).map(note),
  { kind: 'rest', lyric: '(rest)', hint: 'Make a fist to rest. Silence is part of the song.' },
  ...MARY_NOTES.slice(7, 13).map(note),
  { kind: 'swell', lyric: '(louder)', hint: 'Open your hand and push it toward the camera until the note swells.' },
  ...MARY_NOTES.slice(13).map(note),
  { kind: 'loop', lyric: '(pinch)', hint: 'Pinch your thumb and index finger quickly to start recording a loop, play a phrase, then pinch again to close it. Or skip this — a quick pinch on either hand is all it takes.' },
  { kind: 'done', lyric: 'You played it.', hint: 'That is the whole instrument: point, open, push, pinch. Now play whatever you like.' }
]

export class SongLesson {
  private index = 0
  private matchSinceMs: number | null = null
  private articulated = true
  private restSinceMs: number | null = null

  constructor(private readonly steps: LessonStep[] = MARY_LESSON, private readonly title = 'Mary Had a Little Lamb') {}

  get view(): LessonView {
    const step = this.steps[this.index]!
    const notesBefore = this.steps.slice(0, this.index + 1).filter((candidate) => candidate.kind === 'note').length
    return {
      title: this.title,
      step,
      index: this.index,
      total: this.steps.length,
      noteNumber: step.kind === 'note' ? notesBefore : null,
      noteTotal: this.steps.filter((candidate) => candidate.kind === 'note').length,
      complete: step.kind === 'done'
    }
  }

  get target(): number | null {
    const step = this.steps[this.index]!
    return step.kind === 'note' ? step.noteIndex ?? null : null
  }

  observe(observation: LessonObservation, timestampMs: number): boolean {
    const step = this.steps[this.index]!
    switch (step.kind) {
      case 'intro':
        if (observation.handVisible && observation.playing) return this.advance()
        return false
      case 'note': {
        const onTarget = observation.playing && observation.noteIndex === step.noteIndex
        if (!observation.playing || observation.noteIndex !== step.noteIndex) this.articulated = true
        if (onTarget && this.articulated) {
          this.matchSinceMs ??= timestampMs
          if (timestampMs - this.matchSinceMs >= 180) return this.advance()
        } else {
          this.matchSinceMs = null
        }
        return false
      }
      case 'rest':
        if (!observation.playing && observation.handVisible) {
          this.restSinceMs ??= timestampMs
          if (timestampMs - this.restSinceMs >= 400) return this.advance()
        } else {
          this.restSinceMs = null
        }
        return false
      case 'swell':
        if (observation.playing && observation.level >= 0.82) return this.advance()
        return false
      case 'loop':
        if (observation.loopTap) return this.advance()
        return false
      default:
        return false
    }
  }

  skipStep(): boolean {
    if (this.steps[this.index]!.kind === 'done') return false
    return this.advance()
  }

  restart(): void {
    this.index = 0
    this.matchSinceMs = null
    this.restSinceMs = null
    this.articulated = true
  }

  private advance(): boolean {
    const previous = this.steps[this.index]!
    this.index = Math.min(this.steps.length - 1, this.index + 1)
    const next = this.steps[this.index]!
    this.matchSinceMs = null
    this.restSinceMs = null
    // A repeated note must be re-articulated: lift, close, or leave and return.
    this.articulated = !(previous.kind === 'note' && next.kind === 'note' && previous.noteIndex === next.noteIndex)
    return true
  }
}
